import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './StudentLogin.css';
import SchoolIcon from '@mui/icons-material/School';
import { TextField, Button, Alert, CircularProgress } from '@mui/material';

interface StudentLoginProps {
  onLogin: (email: string, password: string) => Promise<void>;
  onSwitchToTeacher: () => void;
}

const StudentLogin: React.FC<StudentLoginProps> = ({ onLogin, onSwitchToTeacher }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await onLogin(email, password);
    } catch (err: any) {
      setError(err.message || t('studentLogin.errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="student-login-container">
      <div className="student-login-card">
        <div className="student-login-header">
          <div className="student-login-logo">
            <SchoolIcon sx={{ fontSize: 48, color: '#1976d2' }} />
            <h1>{t('studentLogin.title')}</h1>
          </div>
          <p className="student-login-subtitle">{t('studentLogin.subtitle')}</p>
        </div>
        
        <div className="student-login-content">
          <h2>{t('studentLogin.welcomeStudent')}</h2>
          <p className="student-login-description">
            {t('studentLogin.description')}
          </p>
          
          <form onSubmit={handleSubmit} className="student-login-form">
            {error && (
              <Alert severity="error" sx={{ marginBottom: 2 }}>
                {error}
              </Alert>
            )}
            
            <TextField
              fullWidth
              label={t('studentLogin.email')}
              placeholder={t('studentLogin.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              required
              disabled={loading}
              margin="normal"
              variant="outlined"
            />
            
            <TextField
              fullWidth
              label={t('studentLogin.password')}
              placeholder={t('studentLogin.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              required
              disabled={loading}
              margin="normal"
              variant="outlined"
            />
            
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ marginTop: 3, padding: 1.5 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} sx={{ marginRight: 1 }} />
                  {t('studentLogin.signingIn')}
                </>
              ) : (
                t('studentLogin.signIn')
              )}
            </Button>
          </form>
          
          <div className="student-login-switch">
            <p>{t('studentLogin.switchToTeacher')}</p>
            <Button
              variant="text"
              onClick={onSwitchToTeacher}
              disabled={loading}
            >
              {t('studentLogin.teacherLogin')}
            </Button>
          </div>
        </div>
        
        <div className="student-login-footer">
          <div className="feature-list">
            <div className="feature-item">
              <span className="feature-icon">📚</span>
              <span>{t('sidebar.myClasses')}</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📝</span>
              <span>{t('sidebar.myAssignments')}</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">✅</span>
              <span>Track Progress</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="student-login-background">
        <div className="background-pattern"></div>
      </div>
    </div>
  );
};

export default StudentLogin;

